<template>
    <v-container fluid>
        <v-layout row wrap>
            <v-flex xs12>
                <v-card>
                    <v-toolbar color="primary" dark>
                        <v-toolbar-title>Gestión de Vehículos</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn color="primary" @click="openCreateDialog" v-if="enableAdvancedShipments">
                            <v-icon left>add</v-icon>
                            Nuevo Vehículo
                        </v-btn>
                    </v-toolbar>

                    <v-card-text>
                        <v-layout row wrap class="mb-3">
                            <v-flex xs12 sm6>
                                <v-text-field
                                    v-model="search"
                                    append-icon="search"
                                    label="Buscar por placa"
                                    single-line
                                    hide-details
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12 sm6>
                                <v-select
                                    v-model="filterType"
                                    :items="vehicleTypes"
                                    item-text="display"
                                    item-value="value"
                                    label="Filtrar por tipo"
                                    clearable
                                    hide-details
                                ></v-select>
                            </v-flex>
                        </v-layout>

                        <v-data-table
                            :headers="headers"
                            :items="filteredVehicles"
                            :loading="loadingVehicles"
                            no-data-text="No hay vehículos registrados"
                            class="elevation-1"
                        >
                            <template v-slot:items="props">
                                <td>{{ props.item.license_plate }}</td>
                                <td>{{ getVehicleTypeDisplay(props.item.type) }}</td>
                                <td>{{ props.item.capacity_tonnage }} Ton</td>
                                <td>{{ props.item.capacity_volume || '-' }} m³</td>
                                <td>
                                    <v-chip :color="props.item.is_active ? 'green' : 'red'" text-color="white" small>
                                        {{ props.item.is_active ? 'Activo' : 'Inactivo' }}
                                    </v-chip>
                                </td>
                                <td>
                                    <v-chip :color="props.item.in_maintenance ? 'orange' : 'blue'" text-color="white" small>
                                        {{ props.item.in_maintenance ? 'En Mantenimiento' : 'Disponible' }}
                                    </v-chip>
                                </td>
                                <td class="text-center">
                                    <v-icon small class="mr-2" @click="openStatusDialog(props.item)" title="Cambiar estado">tune</v-icon>
                                    <v-icon small class="mr-2" @click="openEditDialog(props.item)" title="Editar">edit</v-icon>
                                    <v-icon small @click="openDeleteDialog(props.item)" title="Eliminar">delete</v-icon>
                                </td>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-flex>
        </v-layout>

        <!-- MODAL CREAR/EDITAR VEHÍCULO -->
        <v-dialog v-model="dialogVehicle" max-width="600px" persistent>
            <v-card>
                <v-toolbar color="primary" dark>
                    <v-toolbar-title>{{ isEditing ? 'Editar Vehículo' : 'Nuevo Vehículo' }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-icon @click="closeVehicleDialog">close</v-icon>
                </v-toolbar>

                <v-card-text class="pa-4">
                    <v-layout row wrap>
                        <v-flex xs12>
                            <v-text-field
                                v-model="vehicleForm.license_plate"
                                label="Placa del Vehículo *"
                                prepend-icon="confirmation_number"
                                outlined
                                :error-messages="errors.license_plate"
                            ></v-text-field>
                        </v-flex>

                        <v-flex xs12 sm6>
                            <v-select
                                v-model="vehicleForm.type"
                                :items="vehicleTypes"
                                item-text="display"
                                item-value="value"
                                label="Tipo de Vehículo *"
                                prepend-icon="directions_car"
                                outlined
                                :error-messages="errors.type"
                            ></v-select>
                        </v-flex>

                        <v-flex xs12 sm6>
                            <v-text-field
                                v-model.number="vehicleForm.capacity_tonnage"
                                label="Capacidad (Toneladas) *"
                                type="number"
                                prepend-icon="weight"
                                outlined
                                :error-messages="errors.capacity_tonnage"
                            ></v-text-field>
                        </v-flex>

                        <v-flex xs12 sm6>
                            <v-text-field
                                v-model.number="vehicleForm.capacity_volume"
                                label="Capacidad (Volumen m³)"
                                type="number"
                                prepend-icon="straighten"
                                outlined
                            ></v-text-field>
                        </v-flex>

                        <v-flex xs12 sm6>
                            <v-checkbox
                                v-model="vehicleForm.is_active"
                                label="Vehículo Activo"
                                class="mt-3"
                            ></v-checkbox>
                        </v-flex>

                        <v-flex xs12 sm6>
                            <v-checkbox
                                v-model="vehicleForm.in_maintenance"
                                label="En Mantenimiento"
                                class="mt-3"
                            ></v-checkbox>
                        </v-flex>

                        <v-flex xs12 v-if="vehicleForm.in_maintenance">
                            <v-text-field
                                v-model="vehicleForm.maintenance_notes"
                                label="Notas de Mantenimiento"
                                outlined
                                multi-line
                                rows="3"
                            ></v-text-field>
                        </v-flex>
                    </v-layout>

                    <v-alert v-if="alertMessage" type="error" dismissible class="mt-3">
                        {{ alertMessage }}
                    </v-alert>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="closeVehicleDialog" color="grey">Cancelar</v-btn>
                    <v-btn @click="saveVehicle" color="primary" :loading="loadingSaveVehicle">
                        {{ isEditing ? 'Actualizar' : 'Crear' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- MODAL CONFIRMAR ELIMINACIÓN -->
        <v-dialog v-model="dialogDelete" max-width="400px">
            <v-card>
                <v-card-text class="pa-4">
                    <p class="subheading">¿Estás seguro de que deseas eliminar el vehículo <strong>{{ vehicleToDelete.license_plate }}</strong>?</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="dialogDelete = false" color="grey">Cancelar</v-btn>
                    <v-btn @click="deleteVehicle" color="error" :loading="loadingDelete">Eliminar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- MODAL CAMBIAR ESTADO -->
        <v-dialog v-model="dialogStatus" max-width="500px" persistent>
            <v-card>
                <v-toolbar color="primary" dark>
                    <v-toolbar-title>Cambiar Estado - {{ vehicleToChangeStatus.license_plate }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-icon @click="closeStatusDialog">close</v-icon>
                </v-toolbar>

                <v-card-text class="pa-4">
                    <v-layout row wrap>
                        <v-flex xs12>
                            <v-checkbox
                                v-model="statusForm.is_active"
                                label="Vehículo Activo"
                                color="primary"
                            ></v-checkbox>
                        </v-flex>

                        <v-flex xs12>
                            <v-checkbox
                                v-model="statusForm.in_maintenance"
                                label="En Mantenimiento"
                                color="orange"
                            ></v-checkbox>
                        </v-flex>

                        <v-flex xs12 v-if="statusForm.in_maintenance">
                            <v-text-field
                                v-model="statusForm.maintenance_notes"
                                label="Notas de Mantenimiento"
                                outlined
                                multi-line
                                rows="3"
                                placeholder="Describe el trabajo a realizar..."
                            ></v-text-field>
                        </v-flex>

                        <v-flex xs12 v-if="statusAlertMessage">
                            <v-alert type="error" dismissible>
                                {{ statusAlertMessage }}
                            </v-alert>
                        </v-flex>
                    </v-layout>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="closeStatusDialog" color="grey">Cancelar</v-btn>
                    <v-btn @click="updateVehicleStatus" color="primary" :loading="loadingStatusUpdate">
                        Actualizar Estado
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    import '@/plugins/axios'
    import store from '@/store'
    import { mapState, mapGetters } from 'vuex'
    const axios = require('axios')

    export default {
        name: 'vehicles_management',
        computed: {
            ...mapGetters(['getProfile']),
            ...mapState(['language']),
            enableAdvancedShipments() {
                return this.getProfile && this.getProfile.enable_advanced_shipments
            },
            filteredVehicles() {
                return this.vehicles.filter(vehicle => {
                    const searchMatch = vehicle.license_plate.toLowerCase().includes(this.search.toLowerCase())
                    const typeMatch = !this.filterType || vehicle.type === this.filterType
                    return searchMatch && typeMatch
                })
            }
        },
        data() {
            return {
                vehicles: [],
                loadingVehicles: false,
                search: '',
                filterType: null,
                dialogVehicle: false,
                dialogDelete: false,
                dialogStatus: false,
                isEditing: false,
                loadingSaveVehicle: false,
                loadingDelete: false,
                loadingStatusUpdate: false,
                alertMessage: '',
                statusAlertMessage: '',
                vehicleToDelete: {},
                vehicleToChangeStatus: {},
                statusForm: {
                    is_active: true,
                    in_maintenance: false,
                    maintenance_notes: ''
                },
                vehicleForm: {
                    license_plate: '',
                    type: 'TRACTO',
                    capacity_tonnage: 0,
                    capacity_volume: 0,
                    is_active: true,
                    in_maintenance: false,
                    maintenance_notes: ''
                },
                vehicleFormBackup: null,
                errors: {},
                vehicleTypes: [
                    { value: 'TRACTO', display: 'Tracto' },
                    { value: 'CAJA', display: 'Caja' },
                    { value: 'PLANA', display: 'Plana' },
                    { value: 'CORTINA', display: 'Cortina' },
                    { value: 'TOLVA', display: 'Tolva' },
                    { value: 'CONTENEDOR', display: 'Contenedor' }
                ],
                headers: [
                    { text: 'Placa', value: 'license_plate', sortable: true },
                    { text: 'Tipo', value: 'type', sortable: true },
                    { text: 'Capacidad (Ton)', value: 'capacity_tonnage', sortable: true },
                    { text: 'Volumen (m³)', value: 'capacity_volume', sortable: true },
                    { text: 'Estado', value: 'is_active', sortable: true },
                    { text: 'Mantenimiento', value: 'in_maintenance', sortable: true },
                    { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
                ]
            }
        },
        created() {
            if (this.enableAdvancedShipments) {
                this.loadVehicles()
            }
        },
        methods: {
            loadVehicles() {
                this.loadingVehicles = true
                const empresaId = this.getProfile.id_company
                const config = {
                    method: 'get',
                    url: 'https://empresa-service.intrare.app/vehicles/?empresa=' + empresaId + '&is_active=true',
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': 'Token ' + store.getters.getToken
                    }
                }

                axios(config)
                    .then(response => {
                        this.vehicles = response.data.vehicles || response.data.results || response.data || []
                    })
                    .catch(error => {
                        console.log('Error cargando vehículos:', error)
                        this.alertMessage = 'Error al cargar los vehículos'
                    })
                    .finally(() => {
                        this.loadingVehicles = false
                    })
            },
            openCreateDialog() {
                this.isEditing = false
                this.resetForm()
                this.errors = {}
                this.alertMessage = ''
                this.dialogVehicle = true
            },
            openEditDialog(vehicle) {
                this.isEditing = true
                this.vehicleForm = { ...vehicle }
                this.vehicleFormBackup = vehicle.id
                this.errors = {}
                this.alertMessage = ''
                this.dialogVehicle = true
            },
            openDeleteDialog(vehicle) {
                this.vehicleToDelete = vehicle
                this.dialogDelete = true
            },
            closeVehicleDialog() {
                this.dialogVehicle = false
                this.resetForm()
            },
            resetForm() {
                this.vehicleForm = {
                    license_plate: '',
                    type: 'TRACTO',
                    capacity_tonnage: 0,
                    capacity_volume: 0,
                    is_active: true,
                    in_maintenance: false,
                    maintenance_notes: ''
                }
            },
            validateForm() {
                this.errors = {}
                if (!this.vehicleForm.license_plate) {
                    this.errors.license_plate = ['La placa es requerida']
                }
                if (!this.vehicleForm.type) {
                    this.errors.type = ['El tipo de vehículo es requerido']
                }
                if (!this.vehicleForm.capacity_tonnage || this.vehicleForm.capacity_tonnage <= 0) {
                    this.errors.capacity_tonnage = ['La capacidad debe ser mayor a 0']
                }
                return Object.keys(this.errors).length === 0
            },
            saveVehicle() {
                if (!this.validateForm()) {
                    return
                }

                this.loadingSaveVehicle = true
                this.alertMessage = ''

                if (this.isEditing) {
                    this.updateVehicle()
                } else {
                    this.createVehicle()
                }
            },
            createVehicle() {
                const config = {
                    method: 'post',
                    url: 'https://empresa-service.intrare.app/vehicles/',
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': 'Token ' + store.getters.getToken
                    },
                    data: {
                        empresa: this.getProfile.id_company,
                        type: this.vehicleForm.type,
                        license_plate: this.vehicleForm.license_plate,
                        capacity_tonnage: this.vehicleForm.capacity_tonnage,
                        capacity_volume: this.vehicleForm.capacity_volume || 0,
                        is_active: this.vehicleForm.is_active,
                        in_maintenance: this.vehicleForm.in_maintenance,
                        maintenance_notes: this.vehicleForm.maintenance_notes || ''
                    }
                }

                axios(config)
                    .then(response => {
                        this.vehicles.push(response.data)
                        this.closeVehicleDialog()
                    })
                    .catch(error => {
                        const message = error.response && error.response.data && error.response.data.message
                            ? error.response.data.message
                            : 'Error al crear el vehículo'
                        this.alertMessage = message
                        console.log('Error creando vehículo:', error)
                    })
                    .finally(() => {
                        this.loadingSaveVehicle = false
                    })
            },
            updateVehicle() {
                const config = {
                    method: 'patch',
                    url: 'https://empresa-service.intrare.app/vehicles/' + this.vehicleFormBackup + '/',
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': 'Token ' + store.getters.getToken
                    },
                    data: {
                        type: this.vehicleForm.type,
                        license_plate: this.vehicleForm.license_plate,
                        capacity_tonnage: this.vehicleForm.capacity_tonnage,
                        capacity_volume: this.vehicleForm.capacity_volume || 0,
                        is_active: this.vehicleForm.is_active,
                        in_maintenance: this.vehicleForm.in_maintenance,
                        maintenance_notes: this.vehicleForm.maintenance_notes || ''
                    }
                }

                axios(config)
                    .then(response => {
                        const index = this.vehicles.findIndex(v => v.id === this.vehicleFormBackup)
                        if (index !== -1) {
                            this.vehicles.splice(index, 1, response.data)
                        }
                        this.closeVehicleDialog()
                    })
                    .catch(error => {
                        const message = error.response && error.response.data && error.response.data.message
                            ? error.response.data.message
                            : 'Error al actualizar el vehículo'
                        this.alertMessage = message
                        console.log('Error actualizando vehículo:', error)
                    })
                    .finally(() => {
                        this.loadingSaveVehicle = false
                    })
            },
            deleteVehicle() {
                this.loadingDelete = true
                const config = {
                    method: 'delete',
                    url: 'https://empresa-service.intrare.app/vehicles/' + this.vehicleToDelete.id + '/',
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': 'Token ' + store.getters.getToken
                    }
                }

                axios(config)
                    .then(response => {
                        const index = this.vehicles.findIndex(v => v.id === this.vehicleToDelete.id)
                        if (index !== -1) {
                            this.vehicles.splice(index, 1)
                        }
                        this.dialogDelete = false
                    })
                    .catch(error => {
                        console.log('Error eliminando vehículo:', error)
                        this.alertMessage = 'Error al eliminar el vehículo'
                    })
                    .finally(() => {
                        this.loadingDelete = false
                    })
            },
            openStatusDialog(vehicle) {
                this.vehicleToChangeStatus = vehicle
                this.statusForm = {
                    is_active: vehicle.is_active,
                    in_maintenance: vehicle.in_maintenance,
                    maintenance_notes: vehicle.maintenance_notes || ''
                }
                this.statusAlertMessage = ''
                this.dialogStatus = true
            },
            closeStatusDialog() {
                this.dialogStatus = false
                this.vehicleToChangeStatus = {}
                this.statusForm = {
                    is_active: true,
                    in_maintenance: false,
                    maintenance_notes: ''
                }
                this.statusAlertMessage = ''
            },
            updateVehicleStatus() {
                this.loadingStatusUpdate = true
                this.statusAlertMessage = ''

                const config = {
                    method: 'patch',
                    url: 'https://empresa-service.intrare.app/vehicles/' + this.vehicleToChangeStatus.id + '/status/',
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': 'Token ' + store.getters.getToken
                    },
                    data: {
                        is_active: this.statusForm.is_active,
                        in_maintenance: this.statusForm.in_maintenance,
                        maintenance_notes: this.statusForm.maintenance_notes || ''
                    }
                }

                axios(config)
                    .then(response => {
                        const index = this.vehicles.findIndex(v => v.id === this.vehicleToChangeStatus.id)
                        if (index !== -1) {
                            this.vehicles.splice(index, 1, response.data.vehicle || response.data)
                        }
                        this.closeStatusDialog()
                    })
                    .catch(error => {
                        const message = error.response && error.response.data && error.response.data.message
                            ? error.response.data.message
                            : 'Error al actualizar el estado del vehículo'
                        this.statusAlertMessage = message
                        console.log('Error actualizando estado:', error)
                    })
                    .finally(() => {
                        this.loadingStatusUpdate = false
                    })
            },
            getVehicleTypeDisplay(type) {
                const vehicleType = this.vehicleTypes.find(vt => vt.value === type)
                return vehicleType ? vehicleType.display : type
            }
        }
    }
</script>

<style scoped>
</style>
